import { ChordLib } from '@tunasong/music-lib'
import type { Chord, NoteEvent } from '@tunasong/music-lib'
import type { NoteName } from '@tunasong/schemas'
import { useEffect, useRef, useState } from 'react'
import { usePlayNotes } from '../player/piano-player.hook.js'

const TEMPO_BPM = 100
const BEAT_MS = 60_000 / TEMPO_BPM // 600ms at 100 BPM
const VAMP_CHORD_MS = BEAT_MS // 600ms per chord (one quarter note)
const VAMP_CHORD_DURATION = '4n'
const CHORD_OCTAVE = 4
const VAMP_CHORDS_PER_ITERATION = 4

/** Total length of one vamp loop iteration — 2.4s at 100 BPM, 4 quarter notes. */
export const VAMP_ITERATION_MS = VAMP_CHORDS_PER_ITERATION * VAMP_CHORD_MS

export interface VampStab {
  events: NoteEvent[]
  /** Offset (in ms) from the start of one vamp loop iteration. */
  delayMs: number
}

const chordToEvents = (chord: Chord): NoteEvent[] =>
  ChordLib.getNotes(chord).map(n => ({
    type: 'note' as const,
    note: `${n}${CHORD_OCTAVE}` as NoteName,
    duration: VAMP_CHORD_DURATION,
  }))

/**
 * Build the stab sequence for one vamp iteration. Each stab is one chord at
 * 100 BPM quarter-note spacing. Pure helper — composes nicely with looping
 * scheduling in the hook below.
 */
export const buildVampStabs = (vamp: Chord[]): VampStab[] =>
  vamp.map((chord, index) => ({
    events: chordToEvents(chord),
    delayMs: index * VAMP_CHORD_MS,
  }))

export interface UseModeLabPlaybackProps {
  vamp: Chord[]
  /** When true, the vamp loops indefinitely until `stop()` is called. */
  loop: boolean
}

export interface UseModeLabPlaybackResult {
  play: () => void
  stop: () => void
  isPlaying: boolean
}

/**
 * Looping vamp playback for Mode Lab. Schedules each chord-stab via
 * `setTimeout`. When `loop` is true, a re-entrant scheduling timer fires
 * at the end of each iteration to queue the next one.
 *
 * Different from `useCadenceLabPlayback` (one-shot) by the loop path. Same
 * chord-stab primitive shape so the two hooks share their conceptual model.
 *
 * Notes:
 *  - `play()` reads `vamp` and `loop` from the current render's closure —
 *    a re-render with new props means the next `play()` invocation uses the
 *    new vamp. `<ModeLab>` calls `stop()` and `play()` explicitly when the
 *    user toggles Compare-to-parallel, so the prop change doesn't have to
 *    auto-restart.
 *  - Unmount clears any pending timeouts.
 */
export const useModeLabPlayback = (props: UseModeLabPlaybackProps): UseModeLabPlaybackResult => {
  const { vamp, loop } = props
  const { playNotes } = usePlayNotes()
  const timeoutsRef = useRef<number[]>([])
  const [isPlaying, setIsPlaying] = useState(false)

  const stop = () => {
    timeoutsRef.current.forEach(t => window.clearTimeout(t))
    timeoutsRef.current = []
    setIsPlaying(false)
  }

  const scheduleIteration = () => {
    const stabs = buildVampStabs(vamp)
    if (stabs.length === 0) {
      return
    }
    for (const stab of stabs) {
      const handle = window.setTimeout(() => playNotes(stab.events), stab.delayMs)
      timeoutsRef.current.push(handle)
    }
    if (loop) {
      const loopHandle = window.setTimeout(() => scheduleIteration(), VAMP_ITERATION_MS)
      timeoutsRef.current.push(loopHandle)
    }
  }

  const play = () => {
    stop()
    setIsPlaying(true)
    scheduleIteration()
  }

  useEffect(() => stop, [])

  return { play, stop, isPlaying }
}
